import { credentialsVar } from '../cache/auth'
import { lang } from '../localization'
import { clearSupportDrafts } from './supportDraft'

const RETURN_URL_KEY = 'returnUrl'
const TICKET_DETAIL_PREFIX =
  /^\/(?:(?:en|fa)\/)?support\/tickets\/detail(?:\/|$)/
const TICKET_DETAIL_RETURN_URL =
  /^\/(en|fa)\/support\/tickets\/detail\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

export function buildLoginUrl(returnPath?: string): string {
  const loginPath = `/${lang}/login`
  if (!returnPath || returnPath.includes('/login')) return loginPath
  return `${loginPath}?${RETURN_URL_KEY}=${encodeURIComponent(returnPath)}`
}

export function getReturnUrl(): string | null {
  const params = new URLSearchParams(window.location.search)
  return params.get(RETURN_URL_KEY)
}

export function isSafeReturnUrl(url: string): boolean {
  if (!url.startsWith('/') || url.startsWith('//')) return false
  if (url.includes('\\') || url.includes('?') || url.includes('#')) return false

  // Ticket links originate outside the application, so keep their accepted
  // destination deliberately narrower than ordinary in-app return paths.
  if (TICKET_DETAIL_PREFIX.test(url)) {
    return TICKET_DETAIL_RETURN_URL.test(url)
  }

  return true
}

export function getSafeReturnUrl(): string | null {
  const url = getReturnUrl()
  if (!url) return null
  if (isSafeReturnUrl(url)) return url
  return null
}

// Dedupes concurrent redirects: several requests can 401 at once, and without
// this each one reassigns location.href and the browser races the navigations.
//
// It is a per-NAVIGATION guard, not a per-session one. In the normal path that
// distinction is invisible — location.href unloads the page, so the flag dies
// with the module. It only matters on the branch below that deliberately does
// not navigate; see there.
let isRedirecting = false

export function redirectToLogin(options?: {
  sessionExpired?: boolean
  skipLogoutEvent?: boolean
}): void {
  if (isRedirecting) return
  isRedirecting = true

  if (!options?.skipLogoutEvent) {
    localStorage.setItem('logout', Date.now().toString())
  }
  if (options?.sessionExpired) {
    localStorage.setItem('sessionExpired', 'true')
  }
  credentialsVar(undefined)
  clearSupportDrafts()

  if (!window.location.pathname.includes('/login')) {
    // The page unloads here, taking the latch with it.
    window.location.href = `${window.location.origin}${buildLoginUrl(
      window.location.pathname
    )}`
    return
  }

  // Already on the login screen: credentials are cleared, but there is no
  // navigation to dedupe against, so release the latch.
  //
  // Note what that costs, because the comment on the flag above does NOT hold
  // here: this branch is effectively unguarded. JS is single-threaded and this
  // function never awaits, so a "concurrent" second call can only arrive after
  // the first has run to completion — by which point the latch is already back
  // to false — and it will re-run the whole block. That is safe only because
  // every side effect above is idempotent: clearing an already-cleared
  // reactive var, and overwriting two storage keys with same-shaped values. Do
  // not add a non-idempotent side effect above without re-introducing a guard
  // that survives this branch.
  //
  // Holding it here would make a per-navigation guard permanent, because
  // logging in is an in-place state swap — the route gate is credential-based
  // (credentials?.accessToken ? <MainDrawer /> : <AuthRoutes />), so the app
  // enters the portal with no page load and the module is never re-evaluated.
  // Every later expiry in that page's lifetime would then return at the guard
  // above: no logout broadcast, no sessionExpired flag, no redirect — the user
  // left sitting in a portal whose session is dead.
  isRedirecting = false
}
